import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import { Badge, Box } from "@mui/material";
import { COMMAND_PRIORITY_LOW, KEY_ENTER_COMMAND } from "lexical";
import { useEffect, useState } from "react";
import { useSelectable } from "../../utils/use-selectable";
import { EditorButton } from "./editor-button";

import type { NodeKey } from "lexical";
import type { FC, ReactNode } from "react";

export const EditorActionButtons: FC<{
  disabled: boolean;
  buttons: ("edit" | "delete")[];
  onEditClicked?: () => void;
  onDeleteClicked?: () => void;
  children: ReactNode;
  styles?: { [key: string]: string };
  fullWidth?: boolean | undefined;
  nodeKey: NodeKey;
  "data-analytics-id": string;
}> = ({
  disabled,
  buttons,
  onEditClicked,
  onDeleteClicked,
  children,
  styles,
  fullWidth,
  "data-analytics-id": dataAnalyticsId,
  nodeKey,
}) => {
  const selected = useSelectable(nodeKey);
  const [editor] = useLexicalComposerContext();
  const [hover, setHover] = useState(false);

  useEffect(() => {
    if (onEditClicked)
      return editor.registerCommand(
        KEY_ENTER_COMMAND,
        () => {
          if (selected) onEditClicked();
          return selected;
        },
        COMMAND_PRIORITY_LOW,
      );
    return undefined;
  }, [editor, selected, nodeKey, onEditClicked]);

  return (
    <Box
      onMouseOver={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      sx={{
        userSelect: "none",
      }}
    >
      <Badge
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        sx={{
          gap: "10px",
          ...(selected
            ? {
                backgroundColor: "background.selected",
              }
            : {}),
          "& .MuiBadge-badge": {
            bottom: "50%",
            ...styles,
          },
          ...(fullWidth ? { width: "100%" } : {}),
        }}
        badgeContent={
          (hover || selected) &&
          !disabled && (
            <Box
              flexDirection="column"
              sx={{
                position: "absolute",
                display: "flex",
                gap: "1px",
                marginRight: "-40px",
              }}
            >
              {buttons.includes("edit") && (
                <EditorButton
                  data-analytics-button={`${dataAnalyticsId}-edit`}
                  sx={{ height: 25, width: 25 }}
                  onClick={onEditClicked}
                  icon={<Edit fontSize="small" color="primary" />}
                />
              )}
              {buttons.includes("delete") && (
                <EditorButton
                  data-analytics-button={`${dataAnalyticsId}-delete`}
                  sx={{ height: 25, width: 25 }}
                  onClick={onDeleteClicked}
                  icon={<Delete data-testid="DeleteIcon" fontSize="small" color="primary" />}
                />
              )}
            </Box>
          )
        }
      >
        {children}
      </Badge>
    </Box>
  );
};
